import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import JobCard from '../components/JobCard';
import { API_URL } from '../lib/utils';
import { useAuth } from '../context/AuthContext';
import {
  Search,
  MapPin,
  Briefcase,
  UserCheck,
  ShieldCheck,
  HeartHandshake,
  ArrowRight,
  CheckCircle2,
  Users,
  Sparkles,
  Star,
  Quote,
  Zap,
  Clock,
  Target,
  Shield,
  Building2,
  Heart,
  GraduationCap,
  ChevronRight,
  Stethoscope,
  Building,
} from 'lucide-react';

// SEO Meta Tags
const SEO_TITLE = "Nationwide Social Worker Jobs | LMSW & LCSW Employment | INeedASocialWorker";
const SEO_DESCRIPTION = "INeedASocialWorker.com is a nationwide employment platform for licensed social workers. Connect with hospitals, hospice agencies, schools, and community providers across the United States. Headquartered in Texas.";

const HERO_BG = "/hero-v4.png";
const LOGO_URL = "/inasw-logo-2026.jpg";

// Testimonials data
const testimonials = [
  {
    name: "Maria Gonzalez, LCSW",
    location: "Houston, Texas",
    text: "I found my dream job at a pediatric hospital within 2 weeks of signing up. This site truly understands what social workers need!",
    rating: 5
  },
  {
    name: "David Thompson, LMSW",
    location: "Detroit, Michigan",
    text: "After years of searching general job boards, I finally found a platform made just for us. The quality of listings is outstanding.",
    rating: 5
  },
  {
    name: "Jennifer Ruiz, LCSW",
    location: "Phoenix, Arizona",
    text: "The remote job options helped me find a perfect work-from-home position. Best career decision I've made!",
    rating: 5
  },
  {
    name: "Michael Davis, LBSW",
    location: "Atlanta, Georgia",
    text: "As a new graduate, this site gave me confidence. I landed my first position within a month of getting my license.",
    rating: 5
  },
  {
    name: "Sarah Mitchell, LCSW",
    location: "Denver, Colorado",
    text: "The employers on this site actually value our expertise. I received multiple offers and negotiated a great salary.",
    rating: 5
  },
  {
    name: "James Wilson, LMSW",
    location: "Las Vegas, Nevada",
    text: "Finally, a job board that doesn't mix us in with other healthcare roles. Every listing is relevant to my career.",
    rating: 5
  },
  {
    name: "Amanda Chen, LCSW",
    location: "Chicago, Illinois",
    text: "I was able to transition from agency work to private practice using the connections I made here. Highly recommend!",
    rating: 5
  }
];

const Landing = () => {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const [searchQuery, setSearchQuery] = useState('');
  const [location, setLocation] = useState('');
  const [featuredJobs, setFeaturedJobs] = useState([]);
  const [promotedJobs, setPromotedJobs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchFeaturedJobs();
    
    // Set SEO meta tags
    document.title = SEO_TITLE;
    
    // Update or create meta description
    let metaDescription = document.querySelector('meta[name="description"]');
    if (!metaDescription) {
      metaDescription = document.createElement('meta');
      metaDescription.name = 'description';
      document.head.appendChild(metaDescription);
    }
    metaDescription.content = SEO_DESCRIPTION;
    
    // Add Open Graph tags for social sharing
    const ogTags = [
      { property: 'og:title', content: SEO_TITLE },
      { property: 'og:description', content: SEO_DESCRIPTION },
      { property: 'og:type', content: 'website' },
      { property: 'og:site_name', content: 'INeedASocialWorker.com' },
    ];
    
    ogTags.forEach(({ property, content }) => {
      let tag = document.querySelector(`meta[property="${property}"]`);
      if (!tag) {
        tag = document.createElement('meta');
        tag.setAttribute('property', property);
        document.head.appendChild(tag);
      }
      tag.content = content;
    });
  }, []);

  const fetchFeaturedJobs = async () => {
    try {
      const [regularRes, promotedRes] = await Promise.all([
        axios.get(`${API_URL}/jobs/featured`),
        axios.get(`${API_URL}/jobs/featured-promoted`),
      ]);
      setFeaturedJobs(regularRes.data);
      setPromotedJobs(promotedRes.data);
    } catch (error) {
      console.error('Error fetching featured jobs:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    const params = new URLSearchParams();
    if (searchQuery) params.set('search', searchQuery);
    if (location) params.set('location', location);
    navigate(`/jobs?${params.toString()}`);
  };

  const features = [
    {
      icon: ShieldCheck,
      title: 'Licensed Professionals Only',
      description: 'All positions require valid social work credentials, ensuring qualified candidates.',
    },
    {
      icon: UserCheck,
      title: 'Verified Employers',
      description: 'Connect with reputable healthcare organizations and social service agencies.',
    },
    {
      icon: HeartHandshake,
      title: 'Meaningful Work',
      description: 'Find positions that make a real difference in peoples lives.',
    },
  ];

  const stats = [
    { value: '500+', label: 'Active Jobs' },
    { value: '1000+', label: 'Social Workers' },
    { value: '200+', label: 'Employers' },
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section - faint image watermark + real (scalable/SEO) text */}
      <section
        className="relative min-h-[80vh] flex items-center bg-cover bg-center"
        style={{ backgroundImage: `url(${HERO_BG})` }}
      >
        {/* Faint watermark overlay */}
        <div className="absolute inset-0 bg-primary/85" />

        <div className="relative z-10 w-full">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-16 text-center">
            {/* Real, scalable headline text (SEO + accessible) — centered, 3 lines */}
            <h1 className="font-bold mb-5 leading-tight tracking-tight" style={{ fontFamily: "'Cormorant Garamond', Georgia, serif", textShadow: '0 2px 12px rgba(0,0,0,0.45)' }}>
              <span className="block text-6xl sm:text-7xl lg:text-8xl text-white">Find Your Next</span>
              <span className="block text-6xl sm:text-7xl lg:text-8xl text-secondary">Social Work Career</span>
              <span className="block text-5xl sm:text-6xl lg:text-7xl text-secondary">or Social Work Candidate</span>
            </h1>

            <p className="text-2xl sm:text-3xl font-bold mb-4" style={{ color: '#3b82f6', textShadow: '0 1px 8px rgba(0,0,0,0.4)' }}>
              INeedASocialWorker.com
            </p>

            <p className="text-3xl sm:text-4xl lg:text-5xl text-white font-semibold mb-4 max-w-4xl mx-auto" style={{ fontFamily: "'Cormorant Garamond', Georgia, serif", textShadow: '0 2px 10px rgba(0,0,0,0.45)' }}>
              The Hiring Network Built <span className="font-extrabold text-secondary underline decoration-secondary/60 underline-offset-4">Exclusively</span> for Licensed Social Workers
            </p>

            <p className="text-2xl sm:text-3xl lg:text-4xl text-white font-medium mb-3 max-w-4xl mx-auto" style={{ fontFamily: "'Cormorant Garamond', Georgia, serif", textShadow: '0 2px 10px rgba(0,0,0,0.5)' }} data-testid="hero-tagline">
              The #1 Nationwide Job Site for LCSW, LMSW &amp; LBSW&rsquo;s
            </p>

            <p className="text-xl sm:text-2xl lg:text-3xl text-secondary font-semibold mb-6 max-w-4xl mx-auto" style={{ fontFamily: "'Cormorant Garamond', Georgia, serif", textShadow: '0 2px 10px rgba(0,0,0,0.5)' }} data-testid="hero-subtagline">
              Find Social Work Interns &amp; LCSW Clinical Supervisors
            </p>

            {/* UVP Badge */}
            <div className="inline-flex items-center gap-2 bg-secondary/20 border border-secondary/40 px-4 py-2 rounded-full">
              <Zap className="w-4 h-4 text-secondary" />
              <span className="text-white text-sm font-medium">
                Nationwide • Verified Employers • SW Express Apply™
              </span>
            </div>
          </div>
        </div>
      </section>

      {/* Supervisor & Internship callout */}
      <section className="bg-white py-8 border-b-4 border-secondary" data-testid="home-marketplace-callout">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-extrabold mb-2 text-primary">
            Find an Internship or a Clinical Supervisor
          </h2>
          <p className="text-gray-600 text-base sm:text-lg mb-6 max-w-3xl mx-auto">
            Social work students &amp; LMSWs — connect with verified Texas LCSW-S supervisors and field-placement internships.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link to="/find-a-supervisor" className="w-full sm:w-auto">
              <Button className="w-full sm:w-auto h-14 px-8 text-lg font-bold text-white shadow-lg hover:scale-105 transition-transform" style={{ backgroundColor: '#7B1E24' }} data-testid="home-find-supervisor-btn">
                <ShieldCheck className="w-5 h-5 mr-2" /> Find a Supervisor <ChevronRight className="w-5 h-5 ml-1" />
              </Button>
            </Link>
            <Link to="/internships" className="w-full sm:w-auto">
              <Button className="w-full sm:w-auto h-14 px-8 text-lg font-bold text-white shadow-lg hover:scale-105 transition-transform" style={{ backgroundColor: '#1E3A5F' }} data-testid="home-find-internship-btn">
                <GraduationCap className="w-5 h-5 mr-2" /> Find an Internship <ChevronRight className="w-5 h-5 ml-1" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* One Place for Every Stage flyer */}
      <section className="py-12 bg-white" data-testid="one-place-flyer-section">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-6">
            <h2 className="font-serif text-3xl sm:text-4xl font-extrabold text-primary">One Place for Every Stage of Your Social-Work Career</h2>
            <p className="text-gray-600 text-base sm:text-lg mt-2 max-w-3xl mx-auto">From internship hours and graduation (LBSW, LMSW, LCSW) to licensure, LCSW supervision, and becoming an LCSW-S Supervisor.</p>
          </div>
          <Link to="/internship-portal" className="block group">
            <img
              src="/one-place-career-flyer.jpeg"
              alt="One place for every stage of your social-work career — internship hours, graduation & degrees, licensure, LCSW supervision, LCSW-S supervisor"
              className="w-full max-w-xl mx-auto block group-hover:opacity-95 transition-opacity"
              loading="lazy"
            />
          </Link>
        </div>
      </section>


      {/* Featured Jobs Marquee Bar */}
      <section className="bg-primary py-3 overflow-hidden border-y-4 border-secondary">
        <div className="flex items-center">
          <div className="flex-shrink-0 px-4 bg-secondary text-white font-bold py-2 rounded-r-full">
            FEATURED JOBS
          </div>
          <div className="overflow-hidden flex-1 mx-4">
            {promotedJobs.length > 0 || featuredJobs.length > 0 ? (
              <div className="animate-marquee whitespace-nowrap inline-flex gap-8">
                {[...promotedJobs, ...featuredJobs.slice(0, 5)].map((job, index) => (
                  <Link 
                    key={job.job_id || index} 
                    to={`/jobs/${job.job_id}`}
                    className="inline-flex items-center gap-2 text-white hover:text-secondary transition-colors"
                  >
                    <span className="font-medium">{job.title}</span>
                    <span className="text-white/70">at {job.company_name}</span>
                    <span className="text-secondary">•</span>
                  </Link>
                ))}
                {/* Duplicate for seamless loop */}
                {[...promotedJobs, ...featuredJobs.slice(0, 5)].map((job, index) => (
                  <Link 
                    key={`dup-${job.job_id || index}`} 
                    to={`/jobs/${job.job_id}`}
                    className="inline-flex items-center gap-2 text-white hover:text-secondary transition-colors"
                  >
                    <span className="font-medium">{job.title}</span>
                    <span className="text-white/70">at {job.company_name}</span>
                    <span className="text-secondary">•</span>
                  </Link>
                ))}
              </div>
            ) : (
              <div className="text-center">
                <Link to="/for-employers" className="text-white hover:text-secondary font-medium">
                  ✨ Feature your job with us today! Get more visibility for your open positions ✨
                </Link>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Featured Positions Carousel */}
      {(promotedJobs.length > 0 || featuredJobs.length > 0) && (
        <section className="py-14 bg-gradient-to-b from-white to-gray-50" data-testid="featured-carousel-section">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-end justify-between mb-8">
              <div>
                <p className="text-secondary font-medium tracking-widest uppercase text-sm mb-2 flex items-center gap-2">
                  <Sparkles className="w-4 h-4" /> Featured Positions
                </p>
                <h2 className="font-serif text-3xl sm:text-4xl font-bold text-primary">
                  Spotlighted Social Work Roles
                </h2>
              </div>
              <Link
                to="/jobs"
                className="hidden sm:inline-flex items-center gap-1 text-primary font-semibold hover:text-secondary transition-colors"
                data-testid="carousel-view-all"
              >
                View all jobs <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
            <div className="flex gap-5 overflow-x-auto pb-4 snap-x snap-mandatory scrollbar-hide" data-testid="featured-carousel-track">
              {(promotedJobs.length > 0 ? promotedJobs : featuredJobs).slice(0, 8).map((job, index) => (
                <Link
                  key={job.job_id || index}
                  to={`/jobs/${job.job_id}`}
                  data-testid={`featured-card-${job.job_id || index}`}
                  className="group snap-start shrink-0 w-72 rounded-2xl border border-gray-200 bg-white p-6 shadow-sm transition-all hover:-translate-y-1 hover:shadow-xl hover:border-secondary/50"
                >
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-primary/10 text-primary">
                      <Building2 className="w-5 h-5" />
                    </div>
                    {job.is_featured ? (
                      <Badge className="bg-secondary text-white text-xs flex items-center gap-1">
                        <Star className="w-3 h-3" /> Featured
                      </Badge>
                    ) : (
                      <Badge className="bg-primary/10 text-primary text-xs">{job.license_required}</Badge>
                    )}
                  </div>
                  <h3 className="font-serif text-lg font-bold text-primary leading-snug line-clamp-2 group-hover:text-secondary transition-colors">
                    {job.title}
                  </h3>
                  <p className="mt-1 text-sm text-gray-600 line-clamp-1">{job.company_name}</p>
                  <div className="mt-4 flex flex-wrap items-center gap-x-4 gap-y-2 text-xs text-gray-500">
                    <span className="inline-flex items-center gap-1"><MapPin className="w-3.5 h-3.5" /> {job.location || (job.is_remote ? 'Remote' : 'Various')}</span>
                    <span className="inline-flex items-center gap-1 capitalize"><Briefcase className="w-3.5 h-3.5" /> {(job.job_type || '').replace('-', ' ')}</span>
                  </div>
                  <div className="mt-5 inline-flex items-center gap-1 text-sm font-semibold text-secondary opacity-0 group-hover:opacity-100 transition-opacity">
                    View role <ArrowRight className="w-4 h-4" />
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}


      {/* Why We're Different Section */}
      <section className="py-16 bg-gray-900">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <p className="text-secondary font-medium tracking-widest uppercase text-sm mb-3">
              WHY WE'RE DIFFERENT
            </p>
            <h2 className="font-serif text-3xl sm:text-4xl font-bold text-white mb-4">
              Built Exclusively for Social Workers
            </h2>
            <p className="text-gray-400 max-w-2xl mx-auto">
              The only job board focused exclusively on licensed social work professionals.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 mb-12">
            <div className="bg-gray-800 rounded-xl p-6 border border-gray-700">
              <div className="w-12 h-12 rounded-lg bg-primary/20 flex items-center justify-center mb-4">
                <Shield className="w-6 h-6 text-primary" />
              </div>
              <h3 className="text-lg font-semibold text-white mb-2">Verified Social Work Employers</h3>
              <p className="text-gray-400 text-sm">
                Every employer is reviewed before posting. No spam. No unrelated jobs.
              </p>
            </div>
            
            <div className="bg-gray-800 rounded-xl p-6 border border-gray-700">
              <div className="w-12 h-12 rounded-lg bg-green-500/20 flex items-center justify-center mb-4">
                <Zap className="w-6 h-6 text-green-400" />
              </div>
              <h3 className="text-lg font-semibold text-white mb-2">SW Express Apply™</h3>
              <p className="text-gray-400 text-sm">
                No 15-page applications. No forced profile building. Just apply and track your results.
              </p>
            </div>
          </div>

          {/* Benefits - INeedASocialWorker Only */}
          <div className="bg-gray-800 rounded-xl p-6 border border-gray-700">
            <h4 className="text-secondary text-lg font-semibold mb-4 text-center">On INeedASocialWorker.com</h4>
            <div className="grid md:grid-cols-3 gap-6">
              <div className="text-center">
                <div className="w-10 h-10 rounded-full bg-green-500/20 flex items-center justify-center mx-auto mb-3">
                  <Zap className="w-5 h-5 text-green-400" />
                </div>
                <p className="text-white font-medium">SW Express Apply™</p>
                <p className="text-gray-400 text-sm mt-1">Apply in under 60 seconds</p>
              </div>
              <div className="text-center">
                <div className="w-10 h-10 rounded-full bg-green-500/20 flex items-center justify-center mx-auto mb-3">
                  <Target className="w-5 h-5 text-green-400" />
                </div>
                <p className="text-white font-medium">Only Social Work Jobs</p>
                <p className="text-gray-400 text-sm mt-1">No irrelevant listings</p>
              </div>
              <div className="text-center">
                <div className="w-10 h-10 rounded-full bg-green-500/20 flex items-center justify-center mx-auto mb-3">
                  <CheckCircle2 className="w-5 h-5 text-green-400" />
                </div>
                <p className="text-white font-medium">Track Your Applications</p>
                <p className="text-gray-400 text-sm mt-1">All in one dashboard</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Find Social Work Jobs by Specialty - 6 Cards */}
      <section className="py-16 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="font-serif text-2xl md:text-3xl font-bold text-gray-900 mb-4">
              Find Social Work Jobs by Specialty
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              INeedASocialWorker connects LMSWs, LCSWs, and BSW professionals with verified employers across Texas and throughout the United States. Whether you're seeking hospital, hospice, school, behavioral health, medical, or government positions — find your next opportunity here.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            <Link 
              to="/hospital-social-worker-jobs"
              className="group bg-white rounded-xl border border-gray-200 p-6 hover:border-primary hover:shadow-lg transition-all"
            >
              <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center mb-4 group-hover:bg-primary/20 transition-colors">
                <Building2 className="w-6 h-6 text-primary" />
              </div>
              <h3 className="font-semibold text-gray-900 mb-2 group-hover:text-primary transition-colors">
                Hospital Social Worker Jobs
              </h3>
              <p className="text-sm text-gray-600 mb-4">
                Acute care, discharge planning, ER, ICU, and inpatient medical settings.
              </p>
              <span className="inline-flex items-center text-sm text-primary font-medium">
                View Hospital Jobs <ArrowRight className="w-4 h-4 ml-1" />
              </span>
            </Link>

            <Link 
              to="/hospice-social-worker-jobs"
              className="group bg-white rounded-xl border border-gray-200 p-6 hover:border-primary hover:shadow-lg transition-all"
            >
              <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center mb-4 group-hover:bg-primary/20 transition-colors">
                <Heart className="w-6 h-6 text-primary" />
              </div>
              <h3 className="font-semibold text-gray-900 mb-2 group-hover:text-primary transition-colors">
                Hospice Social Worker Positions
              </h3>
              <p className="text-sm text-gray-600 mb-4">
                Palliative care, home health, travel hospice, and PRN roles.
              </p>
              <span className="inline-flex items-center text-sm text-primary font-medium">
                View Hospice Jobs <ArrowRight className="w-4 h-4 ml-1" />
              </span>
            </Link>

            <Link 
              to="/school-social-worker-jobs"
              className="group bg-white rounded-xl border border-gray-200 p-6 hover:border-primary hover:shadow-lg transition-all"
            >
              <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center mb-4 group-hover:bg-primary/20 transition-colors">
                <GraduationCap className="w-6 h-6 text-primary" />
              </div>
              <h3 className="font-semibold text-gray-900 mb-2 group-hover:text-primary transition-colors">
                School Social Worker Openings
              </h3>
              <p className="text-sm text-gray-600 mb-4">
                Public schools, charter districts, and student support services.
              </p>
              <span className="inline-flex items-center text-sm text-primary font-medium">
                View School Jobs <ArrowRight className="w-4 h-4 ml-1" />
              </span>
            </Link>

            <Link 
              to="/clinical-social-worker-jobs"
              className="group bg-white rounded-xl border border-gray-200 p-6 hover:border-primary hover:shadow-lg transition-all"
            >
              <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center mb-4 group-hover:bg-primary/20 transition-colors">
                <Users className="w-6 h-6 text-primary" />
              </div>
              <h3 className="font-semibold text-gray-900 mb-2 group-hover:text-primary transition-colors">
                Behavioral Health Roles
              </h3>
              <p className="text-sm text-gray-600 mb-4">
                Inpatient psych, outpatient therapy, crisis stabilization, and substance use programs.
              </p>
              <span className="inline-flex items-center text-sm text-primary font-medium">
                View Behavioral Health Jobs <ArrowRight className="w-4 h-4 ml-1" />
              </span>
            </Link>

            <Link 
              to="/medical-social-worker-jobs"
              className="group bg-white rounded-xl border border-gray-200 p-6 hover:border-primary hover:shadow-lg transition-all"
            >
              <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center mb-4 group-hover:bg-primary/20 transition-colors">
                <Stethoscope className="w-6 h-6 text-primary" />
              </div>
              <h3 className="font-semibold text-gray-900 mb-2 group-hover:text-primary transition-colors">
                Medical Social Work Careers
              </h3>
              <p className="text-sm text-gray-600 mb-4">
                Dialysis centers, skilled nursing facilities, rehab, and long-term care.
              </p>
              <span className="inline-flex items-center text-sm text-primary font-medium">
                View Medical Jobs <ArrowRight className="w-4 h-4 ml-1" />
              </span>
            </Link>

            <Link 
              to="/community-social-worker-jobs"
              className="group bg-white rounded-xl border border-gray-200 p-6 hover:border-primary hover:shadow-lg transition-all"
            >
              <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center mb-4 group-hover:bg-primary/20 transition-colors">
                <Building className="w-6 h-6 text-primary" />
              </div>
              <h3 className="font-semibold text-gray-900 mb-2 group-hover:text-primary transition-colors">
                Community & Government Positions
              </h3>
              <p className="text-sm text-gray-600 mb-4">
                State agencies, CPS, VA, nonprofits, and county services.
              </p>
              <span className="inline-flex items-center text-sm text-primary font-medium">
                View Government Jobs <ArrowRight className="w-4 h-4 ml-1" />
              </span>
            </Link>
          </div>

          {/* Browse by Specialty button */}
          <div className="text-center mt-8">
            <Link to="/texas-social-worker-jobs">
              <Button variant="outline" className="border-primary text-primary hover:bg-primary hover:text-white px-8 h-12">
                Browse All Specialties
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* About Section - First Paragraph */}
      <section className="py-12 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-lg text-gray-700 leading-relaxed">
            INeedASocialWorker.com is a nationwide employment platform dedicated exclusively to licensed social work professionals. We connect social workers with hospitals, hospice agencies, school districts, behavioral health organizations, and community providers across the United States. While headquartered in Texas, our network supports employers and job seekers nationwide.
          </p>
        </div>
      </section>

      {/* SEO Content Section - For Social Workers & Employers */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12">
            {/* For Social Workers */}
            <div>
              <h2 className="font-serif text-2xl sm:text-3xl font-bold text-gray-900 mb-6">
                For Social Workers
              </h2>
              <p className="text-gray-700 mb-4">
                Find social work jobs nationwide including:
              </p>
              <ul className="space-y-3 text-gray-700">
                <li className="flex items-center gap-2">
                  <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0" />
                  <span>Hospital Social Worker Jobs</span>
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0" />
                  <span>Hospice Social Worker Positions</span>
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0" />
                  <span>School Social Worker Openings</span>
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0" />
                  <span>Behavioral Health Roles</span>
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0" />
                  <span>Medical Social Work Careers</span>
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0" />
                  <span>Community & Government Agency Positions</span>
                </li>
              </ul>
              <div className="mt-6">
                <Link to="/jobs">
                  <Button className="bg-primary hover:bg-primary-hover text-white">
                    Browse All Jobs
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </Link>
              </div>
            </div>

            {/* For Employers */}
            <div>
              <h2 className="font-serif text-2xl sm:text-3xl font-bold text-gray-900 mb-6">
                For Employers
              </h2>
              <p className="text-gray-700 mb-4">
                Reach licensed social work professionals across the United States. Our platform connects you with qualified LMSWs and LCSWs actively seeking new opportunities.
              </p>
              <ul className="space-y-3 text-gray-700">
                <li className="flex items-center gap-2">
                  <CheckCircle2 className="w-5 h-5 text-secondary flex-shrink-0" />
                  <span>Post jobs in minutes</span>
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle2 className="w-5 h-5 text-secondary flex-shrink-0" />
                  <span>Access verified candidates</span>
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle2 className="w-5 h-5 text-secondary flex-shrink-0" />
                  <span>License verification system</span>
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle2 className="w-5 h-5 text-secondary flex-shrink-0" />
                  <span>In-app messaging with candidates</span>
                </li>
              </ul>
              <div className="mt-6">
                <Link to="/for-employers">
                  <Button className="bg-secondary hover:bg-secondary-hover text-white">
                    Learn More for Employers
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Serving Social Workers Across the U.S. */}
      <section className="py-16 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <h2 className="font-serif text-2xl sm:text-3xl font-bold text-gray-900 mb-4">
              Serving Social Workers Across the U.S.
            </h2>
            <p className="text-gray-700 max-w-3xl mx-auto">
              We support employment opportunities in:
            </p>
          </div>
          <div className="flex flex-wrap justify-center gap-4 mb-8">
            {[
              { state: 'Texas', abbr: 'TX' },
              { state: 'California', abbr: 'CA' },
              { state: 'Florida', abbr: 'FL' },
              { state: 'New York', abbr: 'NY' },
              { state: 'Illinois', abbr: 'IL' },
              { state: 'Georgia', abbr: 'GA' },
            ].map(({ state, abbr }) => (
              <Link 
                key={state}
                to={`/locations/${state.toLowerCase().replace(' ', '-')}`}
                className="px-6 py-3 bg-primary/10 border border-primary/20 rounded-xl text-primary font-medium hover:bg-primary hover:text-white hover:border-primary transition-colors"
              >
                {state}
              </Link>
            ))}
            <span className="px-6 py-3 bg-secondary/10 border border-secondary/20 rounded-xl text-secondary font-medium">
              And expanding nationwide
            </span>
          </div>
          <p className="text-center text-gray-600">
            Employers can post positions from any state, and licensed professionals can explore opportunities across multiple regions.
          </p>
        </div>
      </section>

      {/* Texas-Based. Nationwide Reach. */}
      <section className="py-16 bg-gradient-to-br from-primary to-[#0F2E24]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-serif text-2xl sm:text-3xl font-bold text-white mb-6">
            Texas-Based. Nationwide Reach.
          </h2>
          <p className="text-gray-200 text-lg leading-relaxed mb-8">
            Founded in Texas, INeedASocialWorker was built to address the growing demand for qualified social workers. Our regional expertise provides strong healthcare and educational industry insight while our expanding platform supports hiring nationwide.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/locations">
              <Button className="bg-secondary hover:bg-secondary-hover text-white px-8">
                Explore Locations
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
            <Link to="/jobs">
              <Button variant="outline" className="border-white text-white hover:bg-white hover:text-primary px-8">
                Browse All Jobs
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-16 lg:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <p className="text-secondary font-medium tracking-widest uppercase text-sm mb-3">
              WHY CHOOSE US
            </p>
            <h2 className="font-serif text-3xl sm:text-4xl font-bold text-gray-900 mb-3">
              Built for Social Work Professionals
            </h2>
            <p className="text-gray-600 text-sm">The trusted hiring network for licensed social workers nationwide</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <div
                key={index}
                className="bg-gray-50 p-8 rounded-xl hover:bg-white hover:shadow-lg transition-all duration-200 group"
              >
                <div className="w-14 h-14 rounded-lg bg-primary/10 flex items-center justify-center mb-5 group-hover:bg-primary/20 transition-colors">
                  <feature.icon className="w-7 h-7 text-primary" />
                </div>
                <h3 className="font-serif text-xl font-semibold text-gray-900 mb-3">
                  {feature.title}
                </h3>
                <p className="text-gray-600">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Jobs Section */}
      <section className="py-16 lg:py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-10">
            <div>
              <p className="text-secondary font-medium tracking-widest uppercase text-sm mb-2">
                LATEST OPPORTUNITIES
              </p>
              <h2 className="font-serif text-3xl sm:text-4xl font-bold text-gray-900">
                Featured Jobs
              </h2>
            </div>
            <Link to="/jobs">
              <Button variant="outline" className="border-primary text-primary hover:bg-primary hover:text-white">
                View All Jobs
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </div>

          {loading ? (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {[1, 2, 3, 4].map((i) => (
                <div key={i} className="bg-white rounded-lg p-6 animate-pulse">
                  <div className="flex gap-4">
                    <div className="w-12 h-12 bg-gray-200 rounded-lg" />
                    <div className="flex-1">
                      <div className="h-5 bg-gray-200 rounded w-3/4 mb-2" />
                      <div className="h-4 bg-gray-200 rounded w-1/2" />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <>
              {/* Promoted Jobs (Paid) */}
              {promotedJobs.length > 0 && (
                <div className="mb-8">
                  <div className="flex items-center gap-2 mb-4">
                    <Sparkles className="w-5 h-5 text-secondary" />
                    <span className="text-secondary font-medium">Promoted</span>
                  </div>
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {promotedJobs.map((job) => (
                      <div key={job.job_id} className="relative">
                        <div className="absolute -top-2 -right-2 z-10">
                          <Badge className="bg-secondary text-white">
                            <Sparkles className="w-3 h-3 mr-1" />
                            Featured
                          </Badge>
                        </div>
                        <JobCard job={job} showActions={false} />
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Regular Featured Jobs */}
              {featuredJobs.length > 0 ? (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {featuredJobs.filter(job => !promotedJobs.find(p => p.job_id === job.job_id)).map((job) => (
                    <JobCard key={job.job_id} job={job} showActions={false} />
                  ))}
                </div>
              ) : promotedJobs.length === 0 && (
                <div className="text-center py-12">
                  <Briefcase className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                  <p className="text-gray-500">No jobs available yet. Check back soon!</p>
                </div>
              )}
            </>
          )}
        </div>
      </section>

      {/* Book Promotion Section - Travel Log */}
      <section className="py-12 lg:py-16 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-gradient-to-br from-amber-50 to-orange-50 rounded-2xl overflow-hidden border border-amber-100 p-6 lg:p-8">
            {/* Title beside book */}
            <div className="flex flex-col lg:flex-row items-center gap-6">
              {/* Book Cover */}
              <div 
                className="w-32 lg:w-40 flex-shrink-0 select-none"
                onContextMenu={(e) => e.preventDefault()}
                style={{ pointerEvents: 'none' }}
              >
                <img
                  src="https://customer-assets.emergentagent.com/job_ba288840-867e-4483-82f4-5d28fd248d68/artifacts/c26khoui_ChatGPT%20Image%20Feb%2020%2C%202026%2C%2004_58_41%20PM.png"
                  alt="Home Health & Hospice Travel Log"
                  className="rounded-lg shadow-xl w-full h-auto"
                  draggable="false"
                  style={{ pointerEvents: 'none' }}
                />
              </div>
              {/* Title and description */}
              <div className="flex-1 text-center lg:text-left">
                <div className="inline-flex items-center gap-2 bg-secondary/10 px-3 py-1 rounded-full mb-3">
                  <Sparkles className="w-4 h-4 text-secondary" />
                  <span className="text-secondary text-sm font-medium">Made for Social Workers</span>
                </div>
                <h2 className="font-serif text-2xl lg:text-3xl font-bold text-gray-900 mb-2">
                  Home Health & Hospice Travel Log
                </h2>
                <p className="text-gray-600 mb-3">
                  Beautiful pampas design with enough pages for the entire year! Track mileage, 
                  document visits, manage expenses, and stay organized.
                </p>
                <p className="text-gray-700 font-medium text-sm mb-4">
                  Includes: Expense Records • NVAs • Mileage Tracking • Contacts • Respite Stays • Monthly Calendar
                </p>
                <a 
                  href="https://a.co/d/041VSz1V" 
                  target="_blank" 
                  rel="noopener noreferrer"
                >
                  <Button className="bg-secondary hover:bg-secondary-hover text-white px-6 h-10" data-testid="book-cta">
                    Shop on Amazon
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section for Job Seekers */}
      <section className="py-16 lg:py-24 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Users className="w-12 h-12 text-primary mx-auto mb-6" />
          <h2 className="font-serif text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
            Ready to Advance Your Career?
          </h2>
          <p className="text-gray-600 text-lg mb-8 max-w-2xl mx-auto">
            Create your profile, upload your resume, and start applying to positions 
            that match your skills and career goals.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/jobs">
              <Button variant="outline" className="border-primary text-primary hover:bg-primary hover:text-white px-8 h-12">
                Browse All Jobs
              </Button>
            </Link>
            <Link to={isAuthenticated ? '/dashboard' : '/register'}>
              <Button className="bg-primary hover:bg-primary-hover text-white px-8 h-12" data-testid="create-profile-cta">
                {isAuthenticated ? 'Go to Dashboard' : 'Create Profile'}
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-16 lg:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <p className="text-secondary font-medium tracking-widest uppercase text-sm mb-3">
              SUCCESS STORIES
            </p>
            <h2 className="font-serif text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              Hear From Social Workers Like You
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Join thousands of licensed social workers who found their dream jobs through our platform.
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {testimonials.map((testimonial, index) => (
              <div 
                key={index} 
                className="bg-gray-50 rounded-xl p-6 hover:bg-white hover:shadow-lg transition-all duration-200"
              >
                <div className="flex gap-1 mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <Star key={i} className="w-4 h-4 fill-secondary text-secondary" />
                  ))}
                </div>
                <Quote className="w-8 h-8 text-primary/20 mb-3" />
                <p className="text-gray-700 mb-4 italic">"{testimonial.text}"</p>
                <div className="border-t pt-4">
                  <p className="font-semibold text-gray-900">{testimonial.name}</p>
                  <p className="text-sm text-gray-500">{testimonial.location}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default Landing;
